<template>
	<div id="app" :class="sidebarClass">
		<TreatmentSessionCommentIndicator />
		<StatusIndicator />
		<Modal />
		<Dialog />
		<div v-if="isLoggedIn && !externaluser" data-cy="logged-in">
			<ul class="skip-links sr-only">
				<li>
					<a href="#main" ref="skipLink" class="skip-link">Skip to main content</a>
					<a href="#mainnav" ref="skipLink" class="skip-link">Skip to main navigation</a>
				</li>
			</ul>
			<div class="header" v-if="showMenu" role="banner">
				<img src="./assets/branding/sidebarlogo.png" class="logo" :alt="$t('app-credits')" />
			</div>
			<template v-if="showMenu">
				<button @click="() => toggleMenu()" class="menubtn" v-if="hasMainMenu">
					<b-icon icon="list"></b-icon>
				</button>
				<button @click="() => goBack()" class="menubtn" v-else>
					<b-icon icon="chevron-left" ></b-icon>
				</button>
			</template>

			<div class="sidebar" v-if="showMenu">
				<template v-if="hasMainMenu">
					<SideBarMenuItem
						v-on:click.native="closeMenu"
						link="/"
						:caption="$t('dashboard')"
						icon="house"
					/>
					<template v-if="configloaded">

                        <template v-if="hasModule('timeline')">
                            <SideBarMenuItem
								v-if="hasPriv('TIMELINE_VIEW_ALL') || hasPriv('TIMELINE_VIEW_OWN')"
								v-on:click.native="closeMenu"
								link="/timeline/search"
								:caption="$t('timeline')"
								icon="clock-history"
							/>
                        </template>

						<SideBarMenuItem
							v-if="hasPriv('MESSAGES')"
							v-on:click.native="closeMenu"
							link="/messages/list"
							:caption="$t('messages')"
							icon="inbox"
						/>

						<router-link
							v-if="hasModule('clinical') && hasPriv('TREATMENTSESSION_EDIT')"
							v-on:click.native="closeMenu"
							to="/clinical"
							:aria-label="$t('clinical')"
							><i class="custom-icon icon-tooth"></i>
							<span class="sidebar-hidden-closed" style="margin-left: 10px">{{
								$t("clinical")
							}}</span></router-link
						>

						<SideBarMenuItem
							v-if="hasPriv('TREATMENTSESSION_EDIT') && !$UserSession.hasPriv.call(this, 'MARK_TREATMENTSESSIONS')"
							v-on:click.native="closeMenu"
							link="/clinicalcases/list"
							:caption="$t('clinical-cases')"
							icon="person"
						/>

						<SideBarMenuItem
							v-if="hasPriv('FORMS')"
							v-on:click.native="closeMenu"
							link="/forms"
							:caption="$t('forms')"
							icon="file-earmark-text"
						/>
						<SideBarMenuItem
							v-if="hasPriv('ASSETS')"
							v-on:click.native="closeMenu"
							link="/files"
							:caption="$t('files')"
							icon="images"
						/>
						<SideBarMenuItem
							v-if="hasPriv('TESTS')"
							v-on:click.native="closeMenu"
							link="/tests"
							:caption="$t('tests')"
							icon="award"
						/>

						<SideBarMenuItem
							v-if="hasModule('feedbackcentre') && hasPriv('FEEDBACKCENTRE')"
							v-on:click.native="closeMenu"
							link="/feedback/"
							:caption="$t('feedback')"
							icon="chat-left-quote"
						/>

						<template v-if="hasPriv('REPORT_CLINICALEXPERIENCE')">
							<SideBarMenuItem
								v-if="!hasPriv('REPORT_ALLSTUDENTS')"
								v-on:click.native="closeMenu"
								link="/reports/report/progress"
								:caption="$t('progress')"
								icon="bar-chart-steps"
							/>
							<SideBarMenuItem
								v-if="hasPriv('REPORT_ALLSTUDENTS') || hasPriv('REPORT_MYTREATMENTSESSIONFEEDBACK')"
								v-on:click.native="closeMenu"
								link="/reports/home"
								:caption="$t('reports')"
								icon="bar-chart-steps"
							/>
						</template>

						<template v-if="hasModule('timetable')">

							<SideBarMenuItem
								v-if="hasPriv('TIMETABLE_ATTENDANCE') || hasPriv('PLACEMENT_MY_PLACEMENTS')"
								v-on:click.native="closeMenu"
								link="/attendance"
								:caption="$t('attendance')"
								icon="person-check-fill"
							/>
							<SideBarMenuItem
								v-if="hasPriv('RECORD_ABSENCE') && !hasPriv('TIMETABLE_ATTENDANCE')"
								v-on:click.native="closeMenu"
								link="/attendance/absences"
								:caption="$t('attendance')"
								icon="person-check-fill"
							/>

                            <SideBarMenuItem
								v-if="hasPriv('TIMETABLE_CHECKIN') &&
                                hasPriv('APP_STUDENT') && !hasPriv('ALL')"
								v-on:click.native="closeMenu"
								link="/attendance/checkin/"
								:caption="$t('check-in')"
								icon="person-check-fill"
							/>


							<SideBarMenuItem
								v-if="hasPriv('TIMETABLE_ADMIN') || hasPriv('TIMETABLE_VIEW_ALL')"
								v-on:click.native="closeMenu"
								link="/timetables"
								:caption="$t('timetables')"
								icon="calendar3-range"
							/>
							<template v-else-if="hasPriv('TIMETABLE_VIEW')">
								<SideBarMenuItem
									v-if="hasPriv('APP_TEACHER')"
									v-on:click.native="closeMenu"
									link="/timetables/individualteacher/"
									:caption="$t('timetable')"
									icon="calendar3-range"
								/>
								<SideBarMenuItem
									v-else
									v-on:click.native="closeMenu"
									link="/timetables/individual/"
									:caption="$t('timetable')"
									icon="calendar3-range"
								/>
							</template>

						</template>

						<template v-if="!hasModule('timetable')">
							<SideBarMenuItem
								v-if="hasPriv('RECORD_ABSENCE')"
								v-on:click.native="closeMenu"
								link="/attendance/absences"
								:caption="$t('attendance')"
								icon="person-check-fill"
							/>
						</template>

						<template v-if="$config.getValue('sidebarmenuitems')">
							<template v-for="(item, scindex) in $config.getValue('sidebarmenuitems')">
								<a
									:key="scindex"
									:href="item.link"
									v-if="(!item.priv || hasPriv(item.priv)) && item.link"
									:aria-label="item.caption"
									><b-icon :icon="item.icon"></b-icon
									><span class="sidebar-hidden-closed">{{ item.caption }}</span></a
								>
							</template>
						</template>

					</template>


					<SideBarMenuItem
						v-on:click.native="closeMenu"
						link="/settings"
						icon="gear-fill"
						:caption="$t('settings')"
					/>
					<a href=""
						v-on:click="logOut"
						:aria-label="$t('logout')"
						><b-icon icon="x-square-fill"></b-icon
						><span class="sidebar-hidden-closed">{{ $t("logout") }}</span></a
					>
				</template>

			</div>

			<div class="menumask" @click="menuIsOpen = false"></div>

			<div class="main" id="main" role="main">
				<div class="main-content container-fluid">
					<router-view :key="$route.fullPath" @dataChanged="handleDataChanged" />
				</div>
			</div>
		</div>
		<div v-if="!isLoggedIn && configloaded && !externaluser" class="center" data-cy="login">
			<div class="login-form" v-if="!resetPasswordOpen" @keyup="onLoginKeyUp">
				<div class="text-center mb-4">
					<img src="./assets/branding/logo.png" class="logo" :alt="$t('app-credits')"/>
					<h1 class="sr-only">{{ $t('login') }}</h1>
				</div>
				<div v-if="loginFailed" class="alert alert-danger">{{ $t("login-failed") }}</div>
				<div class="mb-3">
					<label for="login-username" class="sr-only">{{$t('username')}}</label>
					<input
						id="login-username"
						v-model="username"
						type="text"
						:placeholder="$t('username')"
						:aria-label="$t('username')"
						autocomplete="new-password"
						autocorrect="off"
						autocapitalize="off"
						class="form-control"
					/>
				</div>
				<div class="mb-3">
					<label for="login-password" class="sr-only">{{$t('password')}}</label>
					<input
						id="login-password"
						v-model="password"
						type="password"
						:placeholder="$t('password')"
						:aria-label="$t('password')"
						autocomplete="new-password"
						autocorrect="off"
						autocapitalize="off"
						class="form-control"
					/>
				</div>
				<button
					class="btn btn-primary btn-block btn-login mb-4"
					id="login-button"
					v-on:click="tryLogin"
				>
					{{ $t("login") }}
				</button>
				<button
					v-if="$config.hasModule('shibboleth')"
					class="btn btn-secondary btn-block btn-login mb-4"
					v-on:click="loginWithSSO"
				>
					{{ $t("login-with-sso") }}
				</button>
				<div class="text-center">
					<a href="#" @click="resetPassword">{{$t('reset-password')}}</a>
				</div>
				<div class="text-center mt-4">
					<a href="https://www.cafsapp.com">{{$t('app-credits')}}</a>
				</div>
			</div>

			<div class="login-form" v-if="resetPasswordOpen" @keyup="onResetPasswordKeyUp">
				<div class="text-center mb-4">
					<h1>{{ $t("reset-password") }}</h1>
				</div>
				<div v-if="resetPasswordSubmitted" class="alert alert-success">
					{{ $t("reset-password-submitted") }}
				</div>
				<div v-if="resetPasswordError" class="alert alert-danger">
					{{ $t("problem-processing-request-try-again") }}
				</div>
				<div class="mb-3">
					<label for="login-email" class="sr-only">{{$t('email')}}</label>
					<input
						id="login-email"
						v-model="email"
						type="email"
						name="email"
						:placeholder="$t('email')"
						:aria-label="$t('email')"
						autocomplete="off"
						autocorrect="off"
						autocapitalize="off"
						class="form-control"
					/>
					<div class="text-center mt-2 mb-2">{{ $t('--or--') }}</div>
					<label for="login-username" class="sr-only">{{$t('username')}}</label>
					<input
						id="login-username"
						v-model="usernameReset"
						type="text"
						name="username"
						:placeholder="$t('username')"
						:aria-label="$t('username')"
						autocomplete="off"
						autocorrect="off"
						autocapitalize="off"
						class="form-control"
					/>
				</div>
				<button
					class="btn btn-primary btn-block btn-login mb-4"
					v-on:click="tryResetPassword"
				>
					{{ $t("reset-password") }}
				</button>
				<div class="text-center">
					<a href="#" @click="closeResetPassword()">{{ $t("login") }}</a>
				</div>
			</div>
		</div>
		<div v-if="externaluser" class="center">
			<div class="main" id="main" role="main">
				<div class="main-content container-fluid">
					<router-view :key="$route.fullPath" @dataChanged="handleDataChanged"/>
				</div>
			</div>
		</div>
		<BlockingSpinner />
		<NotificationIndicator
			ref="notificationindicator"
			@offline="updateConnectionStatus(false)"
			@online="updateConnectionStatus(true)"
		>
		</NotificationIndicator>

		<div class="application-offline" v-if="offline">
			{{ $t("application-offline") }}
		</div>
	</div>
</template>

<script>
//import AppConfigClass from "./include/config";
import SideBarMenuItem from "./components/SideBarMenuItem";
import StatusIndicator from "./components/StatusIndicator";
import TreatmentSessionCommentIndicator from "./components/TreatmentSessionCommentIndicator";
import Modal from "./components/Modal";
import Dialog from "./components/Dialog";
import BlockingSpinner from "./components/BlockingSpinner";
import NotificationIndicator from "./components/NotificationIndicator";
import retryrequests from "./include/indexeddb/stores/retryrequests";
//import NotExternalUser from "./views/NotExternalUser.vue"
//import { mapState } from 'vuex';

export default {
	data() {
		return {
			configloaded: false,
			username: "",
			password: "",
			email: "",
			usernameReset: "",
			loginFailed: false,
			resetPasswordOpen: false,
			resetPasswordSubmitted: false,
			resetPasswordError: false,
			menuIsOpen: false,
			offline: false,
			hasSSOListener: false,
			confirmBackButton: false,
			externaluser: false
		};
	},
	components: {
		SideBarMenuItem,
		StatusIndicator,
		TreatmentSessionCommentIndicator,
		Modal,
		Dialog,
		BlockingSpinner,
		NotificationIndicator,
	},
	beforeCreate() {
		//var AppConfigObject = new AppConfigClass(this);
		//window.AppConfig = AppConfigObject;
		//window.AppConfig.getConfig();
	},
	created() {},
	mounted() {
		this.tryAuth();
    	this.$root.$on('logOut', (e) => {
      		this.logOut(e);
    	});
		this.isExternalUser();
	},
	methods: {
		updateConnectionStatus(online) {
			this.offline = !online;
		},
		getAppSettings() {

			// Make sure this doesn't block loading if we already have a config cached.
			if(this.$config.hasConfig()){
				this.configloaded = true;
			} else {
				this.$store.commit('showBlockingSpinner');
			}

			var self = this;
			// This tries to load the config from the API, or falls back to cached if there's an issue loading it.
			this.$config.getConfig.call(this, function(){
				if(self.$config.hasConfig()){
					self.configloaded = true;
					self.isExternalUser();
				}
			}, function(){
				if(self.$config.hasConfig()){
					self.configloaded = true;
					self.isExternalUser();
				}
			}).then(() => {
				this.$store.commit('hideBlockingSpinner')
			});
		},
		isExternalUser(){
			if(this.$route.path.startsWith('/externalsignoff/')){
				this.externaluser = true;
			}
		},
		tryAuth() {
			// Try authenticating with the API, but don't throw me out if the API isn't there
			if (this.$UserSession.getValue("authToken")) {
				this.$store.commit("logIn");
				this.getAppSettings();
				this.$refs.notificationindicator.subscribeToNofications();
				this.initRetryRequests();
				this.$cfetch(`${this.$settings.APIBaseURL}/auth`, {
					method: "post",
				}).then((json) => {
					if (json.status === "SUCCESS") {
						json.user.username = this.$UserSession.getValue("username");
						json.user.authToken = this.$UserSession.getValue("authToken");
						this.$UserSession.setUserData.call(this, json.user);
						// Refresh the config here. Doesn't hurt
						this.$config.getConfig.call(this);
						this.$refs.notificationindicator.subscribeToNofications();
					}
				});
			} else {
				this.getAppSettings();
			}
		},
		tryLogin() {
			this.loginFailed = false;
			this.$cfetch(`${this.$settings.APIBaseURL}/login`, {
				authed: false,
				method: "post",
				data: {
					username: this.username,
					password: this.password,
				},
			})
				.then((json) => {
					if (json.status === "SUCCESS") {
						this.menuIsOpen = false;
						this.$UserSession.setUserData.call(this, json.user);
						this.$UserSession.updateValue.call(this, "username", this.username);
						var self = this;
						this.$config.getConfig.call(this, function(){
							if(self.$config.hasConfig()){
								self.configloaded = true;
							}
						}, function(){
							if(self.$config.hasConfig()){
								self.configloaded = true;
							}
						});
						this.username = "";
						this.password = "";
						this.$store.dispatch("clearApiRequests");
						this.$store.commit("logIn");
						this.$refs.notificationindicator.subscribeToNofications();
					} else {
						// Error message here
						this.$UserSession.clearUserData.call(this);
						this.loginFailed = true;
					}
				})
				.catch((error) => {
					this.$UserSession.clearUserData.call(this);
					this.loginFailed = true;
					//this.$emit("error");
					this.$log.error.call(this, error);
				});
		},
		logOut(e) {
			e.preventDefault();
			var self = this;
			this.$store.commit("openDialog", {
				cancelBtn: true,
				title: this.$t("log-out-question"),
				content: this.$t("log-out-of-app-question"),
				confirmCallBack: function () {
					self.$router.push(
						"/",
						function () {
							self.$store.dispatch("clearApiRequests");
							self.$store.commit("logOut");
							self.menuIsOpen = false;
							if(self.$config.hasModule('shibboleth')){
								window.open(`${self.$settings.APIBaseURL}/sso/shibboleth/slorequest`);
							}
						},
						function () {
							self.$store.dispatch("clearApiRequests");
							self.$store.commit("logOut");
							if(self.$config.hasModule('shibboleth')){
								window.open(`${self.$settings.APIBaseURL}/sso/shibboleth/slorequest`);
							}
						}
					);
				},
			});
		},
		handleDataChanged(status){
			this.confirmBackButton = status;
		},
		goBack(){
			if(this.confirmBackButton){
				this.$store.commit("openDialog", {
				cancelBtn: true,
				title: this.$t("quit-without-saving-question"),
				content: this.$t("all-changes-will-be-lost"),
				confirmCallBack: function () {
					this.$router.go(-1);
				},
			});
			} else {
				this.$router.go(-1);
			}
		},
		toggleMenu() {
			this.menuIsOpen = !this.menuIsOpen;
		},
		closeMenu() {
			if (window.innerWidth <= 768) {
				this.menuIsOpen = false;
			}
		},
		hasModule(module) {
			return this.$config.hasModule(module);
		},
		hasPriv(priv) {
			return this.$UserSession.hasPriv.call(this, priv);
		},
		resetPassword() {
			this.resetPasswordOpen = true;
		},
		tryResetPassword() {
			this.resetPasswordSubmitted = false;
			this.$cfetch(`${this.$settings.APIBaseURL}/forgottenpassword`, {
				authed: false,
				method: "get",
				data: {
					email: this.email,
					username: this.usernameReset,
				},
			}).then((json) => {
					if (json.status === "SUCCESS") {
						this.resetPasswordSubmitted = true;
					} else {
						// Error message here
						this.resetPasswordError = true;
					}
				})
				.catch((error) => {
					this.$log.error.call(this, error);
					this.resetPasswordError = true;
				});
		},
		closeResetPassword() {
			this.resetPasswordOpen = false;
			this.resetPasswordSubmitted = false;
			this.email = null;
			this.usernameReset = null;
		},
		onResetPasswordKeyUp(e) {
			if (e.keyCode === 13) {
				this.tryResetPassword();
			}
		},
		onLoginKeyUp(e) {
			if (e.keyCode === 13) {
				this.tryLogin();
			}
		},
		loginWithSSO() {
			this.addSSOEventListener();
			window.open(`${this.$settings.APIBaseURL}/sso/shibboleth/authenticate`);
		},
		addSSOEventListener() {
			if(!this.hasSSOListener){
				this.hasSSOListener = true;
				window.addEventListener('message' , (event)=>{
					if('data' in event && 'ssotoken' in event.data){
						this.$cfetch(`${this.$settings.APIBaseURL}/auth`, {
							method: "post",
							authed: false ,
							data: {
								"username": event.data.username,
								"authToken": event.data.authToken
							}
						}).then((json) => {
							if (json.status === "SUCCESS") {
								json.user.username = event.data.username;
								json.user.authToken = event.data.authToken;
								this.$UserSession.setUserData.call(this, json.user);
								this.$store.commit("logIn");
								this.$config.getConfig.call(this);
								this.$refs.notificationindicator.subscribeToNofications();
								this.initRetryRequests();
							}
						});
						this.tryAuth();
					}
				});
			}
		},
		initRetryRequests() {
			retryrequests.doHousekeeping(24 /* max cache age (hours) */);
			this.retryRequests();
			setInterval(() => {
				this.retryRequests();
			}, 90 /* seconds */ * 1000);
		},
		async retryRequests() {
			const requests = await retryrequests.getRequests();
			for (let request of requests) {
				this.$cfetch(request.url, request.options, request.key)
					/* eslint-disable no-unused-vars */
					.then((json) => {})
					.catch((error) => {});
				/* eslint-enable no-unused-vars */
			}
		},
	},
	computed: {
		hasMainMenu() {
			if('hasmainmenu' in this.$route.meta){
				return this.$route.meta.hasmainmenu;
			}
			return false;
		},
		sidebarClass() {
			if (!this.isLoggedIn && !this.externaluser) {
				return "login";
			}
			if (this.menuIsOpen && this.showMenu) {
				return "sidebar--active";
			}
			if (!this.showMenu || this.externaluser) {
				return "sidebar--hidden";
			}
			return "";
		},
		showMenu() {
			return this.$store.state.showMenu;
		},
		isLoggedIn() {
			return this.$store.state.loggedIn;
		},

	},
};
</script>

<style lang="scss">
@import "./styles/app.scss";
</style>
